import React, {useState, useEffect, useCallback, useRef, useMemo} from "react"
import {useDispatch, useSelector} from "react-redux"
import {useLocation, useHistory} from "react-router-dom"
import _ from "lodash"
import moment from "moment"
import {useAuth0} from "@auth0/auth0-react"

// UI
import {Container} from "@uprise/grid"
import {text} from "@uprise/typography"
import {P} from "@uprise/text"
import {Card} from "@uprise/card"
import {spacing} from "@uprise/spacing"
import {primary} from "@uprise/colors"
import {Alert} from "@uprise/alert"
import {Button} from "@uprise/button"

// Components
import ComponentWrapper from "components/HOC/ComponentWrapper"
import TableComponent from "components/shared/Table"
import {FormElementLabel} from "shared/Form/index.style"
import Checkbox from "components/shared/Checkbox"
import PeriodDropdown from "./periodDropdown"
import IconSearch from "assets/images/icons/svg/search.svg"
import {fetchEmployerData, employerSelector, clearEmployerApiState} from "../../Employers/employerSlice"
import {generateEmployerCodeReports, reportsSelector} from "../reportsSlice"
// Utils
import {getUniqueBy} from "../../Employers/AllEmployers/utils"
// Styles
import {
	HeaderTableContent,
	ColumnContainer,
	SearchInput,
	SearchInputContainer,
	SearchInputfigure,
	SearchInputImage,
	Link
} from "./index.style"

const EmployersTable = () => {
	const location = useLocation()
	const history = useHistory()
	const [token, setToken] = useState(null)
	const [pageCount, setPageCount] = React.useState(0)
	const [employerCodeSearchTermText, setEmployerCodeSearchTermText] = useState(location?.state?.data)
	const [pageNo, setPageNo] = useState(0)
	const [showPeriodDropdown, setShowPeriodDropdown] = useState(false)
	const [allPageResultsSelected, setAllPageResultsSelected] = useState(false)
	const [allResultsSelected, setAllResultsSelected] = useState(false)
	const [showErrorAlert, setShowErrorAlert] = useState(false)
	const [startDate, setStartDate] = useState(null)
	const [reportsTableHeader, setReportsTableHeader] = useState("")
	const [endDate, setEndDate] = useState(null)
	const [reportApiErrorMessage, setReportApiErrorMessage] = useState("")

	const fetchIdRef = useRef(0)
	const searchRef = useRef("")
	const dispatch = useDispatch()
	const {getAccessTokenSilently} = useAuth0()
	const {employers, employerDataApiState} = useSelector(employerSelector)
	const {generateReportsApiState} = useSelector(reportsSelector)

	const [tableData, setTableData] = useState({
		employerRowData: [],
		companyNameFilters: [],
		selectedEmployerDetailsForAction: []
	})

	useEffect(() => {
		// Clear state on unmount
		return () => dispatch(clearEmployerApiState())
	}, [])

	useEffect(() => {
		setPageCount(Math.ceil(employers.total / 20))
	}, [employers])

	const resetDates = () => {
		setEndDate(null)
		setStartDate(null)
	}

	const handleallResultsSelected = () => {
		setAllResultsSelected(true)
		if (!allPageResultsSelected) {
			handleSelectAllRows()
		}
	}

	const handleClearAllResults = () => {
		resetDates()
		setAllResultsSelected(false)
		if (allPageResultsSelected) {
			handleSelectAllRows()
		}
	}

	const fetchData = useCallback(
		({pageSize, pageIndex}) => {
			setAllPageResultsSelected(false)
			const fetchId = ++fetchIdRef.current

			if (searchRef.current !== employerCodeSearchTermText) {
				setAllResultsSelected(false)
				pageIndex = 0
			}

			setPageNo(pageIndex)

			if (fetchId === fetchIdRef.current && token) {
				dispatch(
					fetchEmployerData({
						limit: pageSize,
						skip: pageSize * pageIndex,
						token,
						searchTerm: employerCodeSearchTermText,
						allResultsSelected
					})
				)
			}

			searchRef.current = employerCodeSearchTermText
		},
		[token, employerCodeSearchTermText]
	)

	useEffect(() => {
		async function getToken() {
			const token = await getAccessTokenSilently()
			setToken(token)
		}

		getToken()
	}, [])

	useEffect(() => {
		if (employers.data) {
			const filterArray = getUniqueBy(employers.data, "companyName").map((employer, index) => {
				return {
					id: index,
					label: employer.companyName,
					value: employer.companyName,
					checked: false
				}
			})
			let updatedRowData = employers.data.map(item => ({
				...item,
				selected: true
			}))

			if (allResultsSelected) {
				setAllPageResultsSelected(true)
			}

			setTableData({
				...tableData,
				employerRowData: allResultsSelected ? updatedRowData : employers.data,
				companyNameFilters: filterArray
			})
		}
	}, [employers])

	const handleSearchEmployerCodeChange = useCallback(
		_.debounce(
			value => {
				setEmployerCodeSearchTermText(value)
			},
			1500,
			{maxWait: 1500}
		),
		[]
	)

	const onSelectRow = (code, data) => {
		setShowPeriodDropdown(true)
		let selectedRow = {}
		let selectedData = data.map(item => {
			if (item.code === code) {
				selectedRow = item
				return {...item, selected: !item.selected}
			} else {
				return item
			}
		})
		setAllPageResultsSelected(selectedData.every(row => row.selected))
		allResultsSelected && setAllResultsSelected(selectedData.every(row => row.selected))
		setTableData({
			...tableData,
			selectedEmployerDetailsForAction: selectedRow,
			employerRowData: selectedData
		})
	}

	const handleSelectAllRows = () => {
		allResultsSelected && setAllResultsSelected(false)
		setShowPeriodDropdown(true)
		// Toggle the allPageResultsSelected state
		setAllPageResultsSelected(!allPageResultsSelected)

		// Update the selection status of each row in the employerRowData array
		const updatedData = tableData.employerRowData.map(row => ({
			...row,
			selected: !allPageResultsSelected // Invert the selection status
		}))

		setTableData({
			...tableData,
			employerRowData: updatedData
		})
	}

	const columns = useMemo(
		() => [
			{
				Header: (
					<div style={{display: "flex", alignItems: "left"}}>
						<Checkbox
							data-testid='checkBox'
							checked={allPageResultsSelected}
							iconYPos={"0"}
							onChange={handleSelectAllRows}
						/>
						<span style={{marginLeft: "5px"}}>All</span>
					</div>
				),
				accessor: "id",
				width: "50",
				disableSortBy: true,
				Cell: cellInfo => {
					return (
						<ColumnContainer alignText='left' data-testid='companyName'>
							<Checkbox
								data-testid='checkBox'
								checked={cellInfo.row.original.selected}
								iconYPos={"0"}
								onChange={e => {
									onSelectRow(cellInfo.row.original.code, tableData.employerRowData)
								}}
							/>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Company Name",
				width: "15%",
				accessor: "companyName",
				show: true,
				Cell: cellInfo => {
					return (
						<ColumnContainer alignText='center' data-testid='companyName'>
							<Link
								fontSize={text.t4}
								className='m-l-5'
								color={primary.purple}
								id={cellInfo.row.original.id}
								row={cellInfo.row.original}>
								{cellInfo.row.original.companyName}
							</Link>
						</ColumnContainer>
					)
				}
			},

			{
				Header: "Code",
				accessor: "code",
				show: true,
				Cell: cellInfo => {
					return (
						<ColumnContainer alignText='center'>
							<Link
								fontSize={text.t4}
								className='m-l-5'
								color={primary.purple}
								id={cellInfo.row.original.id}
								row={cellInfo.row.original}>
								{cellInfo.row.original.code}
							</Link>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Number of Employees",
				accessor: "companySize",
				show: true,
				style: {overflow: "auto"},
				Cell: cellInfo => (
					<ColumnContainer alignText='center'>{cellInfo.row.original.companySize}</ColumnContainer>
				)
			},
			{
				Header: "Account Manager",
				accessor: "accountManager",
				show: true,
				style: {overflow: "auto"},
				Cell: cellInfo => (
					<ColumnContainer alignText='center'>
						<P fontSize={text.t4}>{cellInfo.row.original.accountManager}</P>
					</ColumnContainer>
				)
			},
			{
				Header: "Main Contact",
				accessor: "mainContact.label",
				sortType: "string",
				show: true,
				Cell: cellInfo => {
					if (cellInfo.row.original.mainContact.value) {
						return (
							<ColumnContainer alignText='center' data-testid='mainContact'>
								<P
									fontSize={text.t4}
									id={cellInfo.row.original.mainContact.value}
									row={cellInfo.row.original.mainContact.value}>
									{cellInfo.row.original.mainContact.label}
								</P>
							</ColumnContainer>
						)
					} else {
						return <div>{cellInfo.row.original.mainContact.label}</div>
					}
				}
			},
			{
				Header: "Contract Start",
				accessor: d => {
					if (d?.contractStart) {
						return new Date(moment(d.contractStart, ["DD/MM/YYYY", "YYYY-MM-DD"]).toISOString())
					}
					return null
				},
				sortType: "datetime",
				Cell: ({cell: {value}}) => {
					return (
						<ColumnContainer alignText='center'>
							<P fontSize={text.t4}>{value ? moment(value).format("Do MMM YYYY") : "-"}</P>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Contract End",
				accessor: d => {
					if (d?.contractEnd) {
						return new Date(moment(d.contractEnd, ["DD/MM/YYYY", "YYYY-MM-DD"]).toISOString())
					}
					return null
				},
				sortType: "datetime",
				Cell: ({cell: {value}}) => {
					return (
						<ColumnContainer alignText='center'>
							<P fontSize={text.t4}>{value ? moment(value).format("Do MMM YYYY") : "-"}</P>
						</ColumnContainer>
					)
				}
			}
		],
		[token, tableData.employerRowData]
	)

	const selectedRowData = tableData.employerRowData.filter(row => row.selected)

	useEffect(() => {
		if (showErrorAlert) {
			setTimeout(() => {
				setShowErrorAlert(false)
			}, 3000)
		}
	}, [showErrorAlert])

	const handleGenerateReports = async () => {
		const selectedCodes = tableData.employerRowData.reduce((codes, row) => {
			if (row.selected) {
				codes.push(row.code)
			}
			return codes
		}, [])

		const response = await dispatch(
			generateEmployerCodeReports({
				data: {
					searchInput: employerCodeSearchTermText,
					selectedCodes: !allResultsSelected ? selectedCodes : [],
					startDate: startDate.startOf("day"),
					endDate: endDate.endOf("day"),
					selectAllSearchResults: allResultsSelected
				},
				token
			})
		)

		if (!response.payload.error) {
			const data = response.payload.data
			history.push("/reports/employers/generatedReports", {data, token, reportsTableHeader})
		}
		if (response.payload.error) {
			setReportApiErrorMessage(response.payload.error)
			setShowErrorAlert(true)
		}
	}

	const handleCancel = () => {
		resetDates()
		setShowPeriodDropdown(false)
		setAllPageResultsSelected(false)
		setAllResultsSelected(false)
		const updatedData = tableData.employerRowData.map(row => ({
			...row,
			selected: false // Invert the selection status
		}))

		setTableData({
			...tableData,
			employerRowData: updatedData
		})
	}

	return (
		<ComponentWrapper
			menuActiveIndex={5}
			headerTitle='Generate Report'
			headerRightAlignContent={
				<HeaderTableContent>
					<SearchInputContainer>
						<SearchInputfigure>
							<SearchInputImage src={IconSearch} />
						</SearchInputfigure>
						<SearchInput
							type={"text"}
							label={"email"}
							onChange={e => handleSearchEmployerCodeChange(e.target.value)}
							placeholder={"Search by Employer Code, Company Name or Main Contact"}
							data-testid='searchByEmployerCode'
							isRequired={false}
						/>
					</SearchInputContainer>
				</HeaderTableContent>
			}>
			<Container style={{marginBottom: "15px"}}>
				{employerDataApiState.isError && employerDataApiState.message && (
					<Alert type='error'>{employerDataApiState.message} </Alert>
				)}
			</Container>
			<Card padding={spacing.s10} backgroundColor='white' overflow={"unset"}>
				{tableData.employerRowData.length > 0 && (
					<h3 style={{marginBottom: "15px"}}>
						{!allResultsSelected
							? `${employers.total} ${employers.total > 1 ? "Search results." : "Search result."}`
							: `All ${employers.total} search results selected.`}{" "}
						{!allResultsSelected ? (
							<a
								href='#'
								style={{color: "rgb(125,96,255)", textDecoration: "underline", cursor: "pointer"}}
								onClick={handleallResultsSelected}>
								Select all results
							</a>
						) : (
							<a
								href='#'
								style={{color: "rgb(125,96,255)", textDecoration: "underline", cursor: "pointer"}}
								onClick={handleClearAllResults}>
								Clear selection
							</a>
						)}
					</h3>
				)}
				<TableComponent
					pageCount={pageCount}
					fetchData={fetchData}
					pageNo={pageNo}
					columns={columns}
					loading={employerDataApiState.isFetching || !employerDataApiState.isSuccess}
					data={tableData.employerRowData}
				/>

				{showPeriodDropdown && selectedRowData.length > 0 && (
					<div>
						{showErrorAlert && <Alert type='error'>{reportApiErrorMessage}</Alert>}
						<FormElementLabel isRequired={true}>Period</FormElementLabel>
						<div style={{display: "flex", alignItems: "center"}}>
							<PeriodDropdown
								startDate={startDate}
								setStartDate={setStartDate}
								endDate={endDate}
								setEndDate={setEndDate}
								setReportsTableHeader={setReportsTableHeader}
							/>
							<div style={{flex: 1}}></div>
							<Button
								size='small'
								title={"Cancel"}
								variant='tertiary'
								style={{margin: "10px", padding: "20px 24px"}}
								fullWidth={false}
								onClick={handleCancel}
								disabled={generateReportsApiState.isFetching}
							/>
							<Button
								size='small'
								data-testid='createUserButton'
								title={generateReportsApiState.isFetching ? "Loading..." : "Generate"}
								style={{margin: "10px", padding: "20px 24px"}}
								variant='primary'
								fullWidth={false}
								onClick={handleGenerateReports}
								disabled={!startDate || !endDate || generateReportsApiState.isFetching}
							/>
						</div>
					</div>
				)}
			</Card>
		</ComponentWrapper>
	)
}

export default EmployersTable
